// Choix de la juridiction d'un dossier dans la liste du cabinet.
// Tout collaborateur peut ajouter une juridiction absente (« Ajouter … ») ;
// le nettoyage (renommer, fusionner) se fait dans les réglages des dossiers.
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { FaCheck, FaChevronDown, FaPlus } from '../ui/FaIcons';
import { useCreateJurisdiction, useJurisdictions } from '../../hooks/queries/useJurisdictions';
import {
  guessJurisdictionKind,
  JURISDICTION_KINDS,
  JURISDICTION_KIND_LABELS,
  jurisdictionKindLabel,
  jurisdictionMatches,
  sameJurisdictionName,
  type Jurisdiction,
  type JurisdictionKind,
} from '../../services/dossiers/jurisdictionService';

interface Props {
  /** Id de la juridiction choisie, ou null. */
  value: string | null;
  onChange: (jurisdiction: Jurisdiction | null) => void;
  placeholder?: string;
  className?: string;
}

export function JurisdictionSelect({ value, onChange, placeholder = 'Choisir une juridiction', className }: Props) {
  const { data: jurisdictions = [], isLoading } = useJurisdictions();
  const create = useCreateJurisdiction();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<JurisdictionKind | null>(null);

  const selected = jurisdictions.find((j) => j.id === value) ?? null;
  const term = query.trim();
  // Une juridiction désactivée n'est plus proposée, sauf si c'est celle du dossier.
  const options = useMemo(
    () =>
      jurisdictions
        .filter((j) => j.is_active || j.id === value)
        .filter((j) => !term || jurisdictionMatches(j.name, term)),
    [jurisdictions, term, value],
  );
  // Même juridiction écrite autrement (« T.J. Nantes », « Tribunal judiciaire de Nantes ») : on propose l'existante, pas d'ajout.
  const twin = jurisdictions.find((j) => sameJurisdictionName(j.name, term)) ?? null;
  const canAdd = term.length >= 2 && !twin;
  const addKind = kind ?? guessJurisdictionKind(term);

  function pick(jurisdiction: Jurisdiction | null) {
    onChange(jurisdiction);
    setOpen(false);
    setQuery('');
    setKind(null);
  }

  function add() {
    create.mutate(
      { name: term, kind: addKind },
      {
        onSuccess: (jurisdiction) => {
          toast.success(`Juridiction « ${jurisdiction.name} » ajoutée`);
          pick(jurisdiction);
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Impossible d’ajouter la juridiction'),
      },
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-cime border border-neutral-300 bg-white px-3 text-left text-sm transition-colors',
            'hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent',
            className,
          )}
        >
          <span className={cn('min-w-0 truncate', selected ? 'text-neutral-900' : 'text-neutral-400')}>
            {selected ? selected.name : placeholder}
          </span>
          <FaChevronDown className="h-3 w-3 flex-shrink-0 text-neutral-400" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="border-b border-neutral-100 p-2">
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setKind(null);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (twin) pick(twin);
              else if (options.length === 1) pick(options[0] ?? null);
            }}
            placeholder="Chercher ou ajouter : TJ Caen, CA Caen…"
            aria-label="Chercher une juridiction"
            className="h-9 w-full rounded-md border border-neutral-200 px-2.5 text-sm outline-none placeholder:text-neutral-400 focus:border-cime-primary"
          />
        </div>
        <div className="max-h-60 overflow-y-auto py-1" data-menu-scroll>
          {isLoading ? (
            <p className="px-3 py-3 text-sm text-neutral-400">Chargement…</p>
          ) : (
            <>
              {value && !term ? (
                <button
                  type="button"
                  onClick={() => pick(null)}
                  className="w-full px-3 py-2 text-left text-sm italic text-cime-text-muted hover:bg-neutral-50"
                >
                  Aucune juridiction
                </button>
              ) : null}
              {options.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => pick(j)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-50',
                    j.id === value && 'bg-cime-primary/5',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate text-cime-text-primary">{j.name}</span>
                  <span className="flex-shrink-0 text-[11px] text-cime-text-muted">{jurisdictionKindLabel(j.kind)}</span>
                  {j.id === value ? <FaCheck className="h-3 w-3 flex-shrink-0 text-cime-primary" aria-hidden /> : null}
                </button>
              ))}
              {options.length === 0 && !canAdd ? (
                <p className="px-3 py-3 text-sm text-neutral-400">
                  {term ? 'Aucune juridiction à ce nom.' : 'Aucune juridiction enregistrée. Saisissez un nom pour en ajouter une.'}
                </p>
              ) : null}
            </>
          )}
        </div>
        {canAdd ? (
          <div className="flex flex-col gap-2 border-t border-neutral-100 bg-[#fbfaf7] p-2.5">
            <p className="m-0 text-xs text-cime-text-muted">
              Ajouter <b className="text-cime-text-primary">« {term} »</b> à la liste du cabinet
              {options.length > 0 ? ' (vérifiez qu’elle n’existe pas déjà ci-dessus)' : ''} :
            </p>
            <div className="flex items-center gap-2">
              <select
                value={addKind}
                onChange={(e) => setKind(e.target.value as JurisdictionKind)}
                aria-label="Type de juridiction"
                className="h-9 min-w-0 flex-1 rounded-md border border-neutral-200 bg-white px-2 text-sm outline-none focus:border-cime-primary"
              >
                {JURISDICTION_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {JURISDICTION_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={add}
                disabled={create.isPending}
                className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full bg-cime-primary px-3.5 text-[13px] font-medium text-white transition-colors hover:bg-cime-primary/90 disabled:opacity-50"
              >
                <FaPlus className="h-3 w-3" aria-hidden />
                Ajouter
              </button>
            </div>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
